import React, { useState } from 'react';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  XCircleIcon,
  TagIcon,
  FolderIcon,
  HeartIcon,
  ChatBubbleLeftIcon,
  ArrowPathIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const PAGE_SIZE = 50;

const ProjectManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(searchTerm.trim(), 300);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [showCreateTagModal, setShowCreateTagModal] = useState(false);
  const [showProjectDetails, setShowProjectDetails] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const queryClient = useQueryClient();

  // Récupération des projets
  // Recherche, filtres et pagination faits par le serveur (base de données)
  const { data: projectsPage, isLoading } = useQuery(
    ['super-admin-projects', debouncedSearch, statusFilter, categoryFilter, page],
    () => axios.get('/api/super-admin/projects', {
      params: { search: debouncedSearch, status: statusFilter, category: categoryFilter, page, limit: PAGE_SIZE },
    }).then(res => res.data),
    { keepPreviousData: true }
  );
  const projects = projectsPage?.projects;
  const totalProjects = projectsPage?.total || 0;

  // Récupération des catégories et tags
  const { data: categories } = useQuery('super-admin-categories', () => axios.get('/api/super-admin/categories').then(res => res.data));
  useQuery('super-admin-tags', () => axios.get('/api/super-admin/tags').then(res => res.data));

  // Mutations
  const approveProjectMutation = useMutation(
    (projectId) => axios.post(`/api/super-admin/projects/${projectId}/approve`),
    {
      onSuccess: () => {
        toast.success('Projet approuvé avec succès');
        queryClient.invalidateQueries('super-admin-projects');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de l\'approbation');
      }
    }
  );

  const rejectProjectMutation = useMutation(
    ({ projectId, reason }) => axios.post(`/api/super-admin/projects/${projectId}/reject`, { reason }),
    {
      onSuccess: () => {
        toast.success('Projet rejeté avec succès');
        queryClient.invalidateQueries('super-admin-projects');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors du rejet');
      }
    }
  );

  const deleteProjectMutation = useMutation(
    (projectId) => axios.delete(`/api/super-admin/projects/${projectId}`),
    {
      onSuccess: () => {
        toast.success('Projet supprimé avec succès');
        queryClient.invalidateQueries('super-admin-projects');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
      }
    }
  );

  const toggleVisibilityMutation = useMutation(
    ({ projectId, isVisible }) => axios.patch(`/api/super-admin/projects/${projectId}`, { is_visible: isVisible }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('super-admin-projects');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour de la visibilité');
      }
    }
  );

  const toggleVerifiedMutation = useMutation(
    ({ projectId, isVerified }) => axios.patch(`/api/super-admin/projects/${projectId}`, { is_verified: isVerified }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('super-admin-projects');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour du badge');
      }
    }
  );

  const createCategoryMutation = useMutation(
    (categoryData) => axios.post('/api/super-admin/categories', categoryData),
    {
      onSuccess: () => {
        toast.success('Catégorie créée avec succès');
        queryClient.invalidateQueries('super-admin-categories');
        setShowCreateCategoryModal(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la création');
      }
    }
  );

  const createTagMutation = useMutation(
    (tagData) => axios.post('/api/super-admin/tags', tagData),
    {
      onSuccess: () => {
        toast.success('Tag créé avec succès');
        queryClient.invalidateQueries('super-admin-tags');
        setShowCreateTagModal(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la création');
      }
    }
  );

  const statuses = [
    { value: 'pending', label: 'En attente', color: 'yellow' },
    { value: 'approved', label: 'Approuvé', color: 'green' },
    { value: 'rejected', label: 'Rejeté', color: 'red' },
    { value: 'draft', label: 'Brouillon', color: 'gray' }
  ];

  const handleSelectProject = (project) => {
    setSelectedProjects(prev => 
      prev.includes(project.id) 
        ? prev.filter(id => id !== project.id)
        : [...prev, project.id]
    );
  };

  const handleSelectAll = () => {
    if (selectedProjects.length === projects?.length) {
      setSelectedProjects([]);
    } else {
      setSelectedProjects(projects?.map(project => project.id) || []);
    }
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
    setShowProjectDetails(true);
  };

  const handleApproveProject = (projectId) => {
    if (window.confirm('Êtes-vous sûr de vouloir approuver ce projet ?')) {
      approveProjectMutation.mutate(projectId);
    }
  };

  const handleRejectProject = (projectId) => {
    const reason = prompt('Raison du rejet (optionnel):');
    if (reason !== null) {
      rejectProjectMutation.mutate({ projectId, reason });
    }
  };

  const handleDeleteProject = (projectId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) {
      deleteProjectMutation.mutate(projectId);
    }
  };

  const handleToggleVisibility = (project) => {
    toggleVisibilityMutation.mutate({ projectId: project.id, isVisible: !project.isVisible });
  };

  const handleToggleVerified = (project) => {
    toggleVerifiedMutation.mutate({ projectId: project.id, isVerified: !project.isVerified });
  };

  const getStatusBadge = (status) => {
    const statusConfig = statuses.find(s => s.value === status);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusConfig?.color}-100 text-${statusConfig?.color}-800`}>
        {statusConfig?.label}
      </span>
    );
  };

  const CreateCategoryModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Créer une nouvelle catégorie</h3>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            const categoryData = {
              name: formData.get('name'),
              description: formData.get('description'),
              color: formData.get('color'),
              icon: formData.get('icon')
            };
            createCategoryMutation.mutate(categoryData);
          }}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nom de la catégorie</label>
                <input type="text" name="name" required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea name="description" rows="3" className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"></textarea>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Couleur</label>
                <input type="color" name="color" defaultValue="#3B82F6" className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Icône</label>
                <input type="text" name="icon" placeholder="Nom de l'icône Heroicons" className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowCreateCategoryModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={createCategoryMutation.isLoading}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
              >
                {createCategoryMutation.isLoading ? 'Création...' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const CreateTagModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Créer un nouveau tag</h3>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            const tagData = {
              name: formData.get('name'),
              color: formData.get('color')
            };
            createTagMutation.mutate(tagData);
          }}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nom du tag</label>
                <input type="text" name="name" required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Couleur</label>
                <input type="color" name="color" defaultValue="#6B7280" className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowCreateTagModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={createTagMutation.isLoading}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
              >
                {createTagMutation.isLoading ? 'Création...' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const ProjectDetailsModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Détails du projet</h3>
            <button
              onClick={() => setShowProjectDetails(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="sr-only">Fermer</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {selectedProject && (
            <div className="space-y-6">
              {/* En-tête du projet */}
              <div className="border-b pb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedProject.title}</h2>
                    <p className="text-gray-600 mt-1">{selectedProject.description}</p>
                    <div className="flex items-center space-x-4 mt-2">
                      {getStatusBadge(selectedProject.status)}
                      <span className="text-sm text-gray-500">
                        Par {selectedProject.authorName}
                      </span>
                      <span className="text-sm text-gray-500">
                        {new Date(selectedProject.createdAt).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    {selectedProject.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApproveProject(selectedProject.id)}
                          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
                        >
                          <CheckCircleIcon className="h-4 w-4 mr-2" />
                          Approuver
                        </button>
                        <button
                          onClick={() => handleRejectProject(selectedProject.id)}
                          className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 flex items-center"
                        >
                          <XCircleIcon className="h-4 w-4 mr-2" />
                          Rejeter
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Contenu du projet */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Description détaillée</h4>
                    <div className="prose max-w-none">
                      <p className="text-gray-600 whitespace-pre-wrap">{selectedProject.detailedDescription}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Objectifs</h4>
                    <div className="prose max-w-none">
                      <p className="text-gray-600 whitespace-pre-wrap">{selectedProject.objectives}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Stratégie de mise en œuvre</h4>
                    <div className="prose max-w-none">
                      <p className="text-gray-600 whitespace-pre-wrap">{selectedProject.implementationStrategy}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Informations financières */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Informations financières</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Budget demandé:</span>
                        <span className="text-sm font-medium">{selectedProject.budgetRequested} {selectedProject.currency}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Budget déjà obtenu:</span>
                        <span className="text-sm font-medium">{selectedProject.budgetObtained || 0} {selectedProject.currency}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">ROI attendu:</span>
                        <span className="text-sm font-medium">{selectedProject.expectedROI}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Statistiques */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Statistiques</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <HeartIcon className="h-4 w-4 text-red-500 mr-2" />
                          <span className="text-sm text-gray-600">Likes</span>
                        </div>
                        <span className="text-sm font-medium">{selectedProject.likesCount || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <ChatBubbleLeftIcon className="h-4 w-4 text-blue-500 mr-2" />
                          <span className="text-sm text-gray-600">Commentaires</span>
                        </div>
                        <span className="text-sm font-medium">{selectedProject.commentsCount || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <EyeIcon className="h-4 w-4 text-gray-500 mr-2" />
                          <span className="text-sm text-gray-600">Vues</span>
                        </div>
                        <span className="text-sm font-medium">{selectedProject.viewsCount || 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Catégories et tags */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Catégories et tags</h4>
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm text-gray-600">Catégorie:</span>
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {selectedProject.categoryName || 'Non définie'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Tags:</span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {selectedProject.tags?.map(tag => (
                            <span key={tag.id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {tag.name}
                            </span>
                          )) || <span className="text-sm text-gray-500">Aucun tag</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des projets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion des projets</h1>
              <p className="mt-2 text-gray-600">
                Gérez tous les projets et leur modération
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => queryClient.invalidateQueries(['super-admin-projects', 'super-admin-categories', 'super-admin-tags'])}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors flex items-center"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Actualiser
              </button>
              <button
                onClick={() => setShowCreateCategoryModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center"
              >
                <FolderIcon className="h-5 w-5 mr-2" />
                Nouvelle catégorie
              </button>
              <button
                onClick={() => setShowCreateTagModal(true)}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors flex items-center"
              >
                <TagIcon className="h-5 w-5 mr-2" />
                Nouveau tag
              </button>
            </div>
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un projet..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">Tous les statuts</option>
              {statuses.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
            
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">Toutes les catégories</option>
              {categories?.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {totalProjects} projet(s)
              </span>
              {selectedProjects.length > 0 && (
                <span className="text-sm text-primary">
                  {selectedProjects.length} sélectionné(s)
                </span>
              )}
            </div>
            {totalProjects > PAGE_SIZE && (
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="px-2 py-1 text-sm border border-gray-300 rounded disabled:opacity-40"
                >
                  Précédent
                </button>
                <span className="text-sm text-gray-600">Page {page} / {Math.max(1, Math.ceil(totalProjects / PAGE_SIZE))}</span>
                <button
                  type="button"
                  disabled={page * PAGE_SIZE >= totalProjects}
                  onClick={() => setPage(p => p + 1)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded disabled:opacity-40"
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tableau des projets */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedProjects.length === projects?.length && projects?.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Projet
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Auteur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Budget
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Visible
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vérifié
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {projects?.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedProjects.includes(project.id)}
                        onChange={() => handleSelectProject(project)}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
                            <DocumentTextIcon className="h-6 w-6 text-white" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                            {project.title}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {project.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-xs font-medium text-gray-700">
                              {(project.author?.name || project.authorName || 'U')[0]}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {project.author?.name || project.authorName || 'Utilisateur'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(project.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {project.budgetRequested} {project.currency}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleVisibility(project)}
                        title={project.isVisible ? 'Masquer le projet' : 'Afficher le projet'}
                        className={project.isVisible ? 'text-green-600 hover:text-green-800' : 'text-gray-400 hover:text-gray-600'}
                      >
                        {project.isVisible
                          ? <EyeIcon className="h-5 w-5" />
                          : <EyeSlashIcon className="h-5 w-5" />
                        }
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleVerified(project)}
                        title={project.isVerified ? 'Retirer le badge Vérifié' : 'Marquer comme Vérifié par Innovactive360'}
                        className={project.isVerified ? 'text-blue-600 hover:text-blue-800' : 'text-gray-300 hover:text-blue-400'}
                      >
                        <ShieldCheckIcon className="h-5 w-5" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {project.createdAt ? new Date(project.createdAt).toLocaleDateString('fr-FR') : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewProject(project)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Voir les détails"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        {project.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApproveProject(project.id)}
                              className="text-green-600 hover:text-green-900"
                              title="Approuver"
                            >
                              <CheckCircleIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleRejectProject(project.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Rejeter"
                            >
                              <XCircleIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDeleteProject(project.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions en lot */}
        {selectedProjects.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {selectedProjects.length} projet(s) sélectionné(s)
              </span>
              <div className="flex space-x-2">
                <button className="px-3 py-1 bg-green-100 text-green-800 rounded-md text-sm hover:bg-green-200">
                  Approuver
                </button>
                <button className="px-3 py-1 bg-red-100 text-red-800 rounded-md text-sm hover:bg-red-200">
                  Rejeter
                </button>
                <button className="px-3 py-1 bg-red-100 text-red-800 rounded-md text-sm hover:bg-red-200">
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modales */}
      {showCreateCategoryModal && <CreateCategoryModal />}
      {showCreateTagModal && <CreateTagModal />}
      {showProjectDetails && <ProjectDetailsModal />}
    </div>
  );
};

export default ProjectManagement;



